import { apiClient } from './axios';
import { ICustomer } from './customers';
import { IEmployee } from './employee';
import { IGift } from './gifts';

export enum GiftRequestStatus {
  NEW = 'new',
  PENDING = 'pending',
  BUSINESS_APPROVED = 'business_approved',
  BUSINESS_REJECTED = 'business_rejected',
  GENERAL_DIRECTOR_APPROVED = 'general_director_approved',
  GENERAL_DIRECTOR_REJECTED = 'general_director_rejected',
  ADMIN_APPROVED = 'admin_approved',
  ADMIN_REJECTED = 'admin_rejected',
  COMPLETED = 'completed',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum CertificateStatus {
  PENDING = 'pending',
  DONE = 'done',
  FAILED = 'failed',
}

export interface ICertificateParams {
  fileNames: {
    name: string;
    type: string;
  }[];
}

export interface IGiftRequest {
  id: string;
  gift: IGift;
  customers: ICustomer[];
  reason: string;
  status: GiftRequestStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  createdBy: IEmployee;
  assignedTo: IEmployee;
  numberOfGiftsPerCustomer: number;
  reasonReject: string;
  explanation: string;
  usedRejectBy: IEmployee | null;
  timeReject: string | null;
}

export interface GiftRequestResponse {
  items: IGiftRequest[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

export interface GiftRequestFormData {
  giftId: number;
  customerIds: number[];
  reason: string;
  status?: GiftRequestStatus;
  assignedToId?: number;
  numberOfGiftsPerCustomer?: number;
  reasonReject?: string;
  explanation?: string;
}
export const getGiftRequests = async (
  page: number,
  limit: number,
  employeeId: string | null,
  status: GiftRequestStatus | null,
  customerId: string | null,
  search: string
): Promise<GiftRequestResponse> => {
  const response = await apiClient.get<GiftRequestResponse>('gift-requests', {
    params: {
      page,
      limit,
      employeeId,
      status,
      customerId,
      search,
    },
  });
  return response.data;
};

export const createGiftRequest = async (
  giftRequest: GiftRequestFormData
): Promise<IGiftRequest> => {
  const response = await apiClient.post<IGiftRequest>(
    'gift-requests',
    giftRequest
  );
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export const updateGiftRequest = async (
  id: string,
  giftRequest: GiftRequestFormData
): Promise<IGiftRequest> => {
  const response = await apiClient.patch<IGiftRequest>(
    `gift-requests/${id}`,
    giftRequest
  );
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export const deleteGiftRequest = async (id: string) => {
  const response = await apiClient.delete(`gift-requests/${id}`);
  if (!response.success && response?.error) {
    throw new Error(response.message);
  }
  return response.data;
};

export const approveGiftRequest = async (
  id: string,
  status: GiftRequestStatus,
  reasonReject?: string
): Promise<IGiftRequest> => {
  const response = await apiClient.patch<IGiftRequest>(
    `gift-requests/${id}/status`,
    { status, reasonReject }
  );
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export interface IGenerateCertificateResponse {
  url: string;
  key: string;
  uploadUrl: string;
}

export const generateCertificate = async (
  id: string,
  payload: ICertificateParams
): Promise<IGenerateCertificateResponse[]> => {
  const response = await apiClient.post<IGenerateCertificateResponse[]>(
    `gift-requests/${id}/generate-certificate`,
    payload
  );
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export interface ICertificateIdsAndStatuses {
  url: string;
  status: CertificateStatus;
}

export const updateCertificateStatus = async (
  payload: ICertificateIdsAndStatuses[]
) => {
  const response = await apiClient.patch<ICertificate[]>(
    `certificates/update-status`,
    {
      certificateIdsAndStatuses: payload,
    }
  );
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

///api/acceefiirstt;

export enum AcceptanceRequestStatus {
  PENDING = 'pending',
  DONE = 'done',
  FAILED = 'failed',
}

export interface ICertificate {
  id: string;
  url: string;
  status: AcceptanceRequestStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  name: string;
  giftRequest: IGiftRequest;
}

export interface ICertificateResponse {
  items: ICertificate[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

export const getCertificatesRequests = async (
  giftRequestId: string
): Promise<ICertificateResponse> => {
  const response = await apiClient.get<ICertificateResponse>('/certificates', {
    params: {
      page: 1,
      limit: 100,
      giftRequestId,
    },
  });
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export const sendToManagerMarketing = async (id: string): Promise<void> => {
  await apiClient.patch(`gift-requests/${id}/send-to-manager`);
};

// Statistics Overview Types
export interface EmployeeStatistics {
  employeeId: number;
  employeeName: string;
  totalGiftsGiven: number;
  totalValue: number;
  numberOfRequests: number;
  averageValuePerGift: number;
}

export interface GiftReceived {
  giftId: number;
  giftName: string;
  quantity: number;
  value: number;
  receivedDate: string;
}

export interface CustomerStatistics {
  customerId: number | null;
  customerName: string | null;
  totalGiftsReceived: number;
  totalValue: number;
  numberOfRequests: number;
  giftsReceived: GiftReceived[];
}

export interface GiftRequestsStatisticsOverviewResponse {
  employeeStatistics: EmployeeStatistics[];
  customerStatistics: CustomerStatistics[];
}

export interface GiftRequestsStatisticsOverviewParams {
  startDate?: string;
  endDate?: string;
  employeeId?: number;
  customerId?: number;
}

// New interfaces for monthly statistics
export interface MonthlyData {
  month: number;
  monthName: string;
  totalRequests: number;
  totalGiftsGiven: number;
  totalValue: number;
}

export interface EmployeeMonthlyStatistics {
  year: number;
  employeeId: number;
  employeeName: string;
  monthlyData: MonthlyData[];
  totalGiftsGiven: number;
  totalValue: number;
  totalRequests: number;
}

export const getEmployeeMonthlyStatistics = async (
  employeeId: number,
  year: number
): Promise<EmployeeMonthlyStatistics> => {
  const response = await apiClient.get<EmployeeMonthlyStatistics>(
    `gift-requests/statistics/employee/${employeeId}/monthly/${year}`
  );
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export interface CustomerMonthlyStatistics {
  year: number;
  customerId: number;
  customerName: string;
  monthlyData: MonthlyData[];
  totalGiftsReceived: number;
  totalValue: number;
  totalRequests: number;
}

export interface GiftRequestTotalStatistics {
  year: number;
  monthlyData: MonthlyData[];
  totalGiftsGiven: number;
  totalValue: number;
  totalRequests: number;
  averageValuePerGift: number;
}

export const getGiftRequestTotalStatistics = async (
  year: number
): Promise<GiftRequestTotalStatistics> => {
  const response = await apiClient.get<GiftRequestTotalStatistics>(
    `gift-requests/statistics/total/yearly/${year}`
  );
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};
